import React, { useEffect, useState, useMemo } from "react";
import {
  FaTable,
  FaTh,
  FaCheckCircle,
  FaHourglassHalf,
  FaTimesCircle,
  FaSync,
  FaCalendarAlt,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import PageHeader from "../../core/common/header/PageHeader";

const statusColors: Record<string, string> = {
  PENDING_PAYMENT: "bg-orange-100 text-orange-800",
  PENDING: "bg-yellow-100 text-yellow-800",
  INVITED: "bg-blue-100 text-blue-800",
  CONFIRMED: "bg-green-100 text-green-800",
  COMPLETED: "bg-green-700 text-white",
  CANCELLED: "bg-red-100 text-red-800",
};

interface InvitationData {
  appointmentTime: string;
  meetingLink: string;
}

const ManageAppointments: React.FC = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"appointments" | "payments">("payments");
  const [viewMode, setViewMode] = useState<"table" | "card">("table");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [topicFilter, setTopicFilter] = useState<string>("ALL");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteAppointmentId, setInviteAppointmentId] = useState<number | null>(
    null
  );
  const [invitationData, setInvitationData] = useState<InvitationData>({
    appointmentTime: "",
    meetingLink: "",
  });
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignAppointmentId, setAssignAppointmentId] = useState<number | null>(
    null
  );
  const [assignableStudents, setAssignableStudents] = useState<any[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null
  );
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any | null>(null);

const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectAppointmentId, setRejectAppointmentId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [paymentActionLoading, setPaymentActionLoading] = useState(false);

  const [approveConfirmModalOpen, setApproveConfirmModalOpen] = useState(false);
  const [approveConfirmId, setApproveConfirmId] = useState<number | null>(null);

  const [revertConfirmModalOpen, setRevertConfirmModalOpen] = useState(false);
  const [revertConfirmId, setRevertConfirmId] = useState<number | null>(null);

  const fetchAppointments = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setAppointments(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, topicFilter]);

  const allTopics = useMemo(
    () =>
      Array.from(
        new Set(
          appointments
            .map((a) => a.appointmentTopic)
            .filter((t) => t && t !== "")
        )
      ),
    [appointments]
  );

  const filteredAppointments = useMemo(() => {
    let arr = [...appointments];
    if (statusFilter !== "ALL") {
      arr = arr.filter((a) => a.status === statusFilter);
    }
    if (topicFilter !== "ALL") {
      arr = arr.filter((a) => a.appointmentTopic === topicFilter);
    }
    return arr.sort((a, b) => b.id - a.id);
  }, [appointments, statusFilter, topicFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAppointments = filteredAppointments.slice(
    startIndex,
    endIndex
  );

  // ===== Correct totals (computed from the FULL list, not filtered) =====
  const total = appointments.length;
  const totalPending = appointments.filter(
    (a) => a.status === "PENDING"
  ).length;
  const totalInvited = appointments.filter(
    (a) => a.status === "INVITED"
  ).length;
  const totalConfirmed = appointments.filter(
    (a) => a.status === "CONFIRMED"
  ).length;
  const totalCompleted = appointments.filter(
    (a) => a.status === "COMPLETED"
  ).length;
  const totalCancelled = appointments.filter(
    (a) => a.status === "CANCELLED"
  ).length;
  const totalPendingPayment = appointments.filter(
    (a) => a.status === "PENDING_PAYMENT"
  ).length;

  const openInviteModal = (appointmentId: number, a: any) => {
    setInviteAppointmentId(appointmentId);
    setInvitationData({
      appointmentTime: a?.appointmentTime ? a.appointmentTime.slice(0, 16) : "",
      meetingLink: a?.meetingLink || "",
    });
    setInviteError(null);
    setInviteModalOpen(true);
  };

  const closeInviteModal = () => {
    setInviteModalOpen(false);
    setInviteAppointmentId(null);
    setInvitationData({ appointmentTime: "", meetingLink: "" });
    setInviteError(null);
  };

  const handleInviteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInvitationData({ ...invitationData, [e.target.name]: e.target.value });
  };

  const sendInvitation = async () => {
    if (!inviteAppointmentId) return;
    setInviteError(null);
    if (!invitationData.appointmentTime) {
      setInviteError("Please select appointment date and time.");
      return;
    }
    if (!invitationData.meetingLink.trim()) {
      setInviteError("Please enter the meeting link.");
      return;
    }
    setInviting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments/${inviteAppointmentId}/invite`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            appointmentTime: invitationData.appointmentTime,
            meetingLink: invitationData.meetingLink.trim(),
          }),
        }
      );

      if (!res.ok) {
        let errorMsg = "Failed to send invitation.";
        try {
          const errorData = await res.json();
          errorMsg = errorData.message || errorMsg;
        } catch {}
        setInviteError(errorMsg);
        setInviting(false);
        return;
      }

      const updatedAppointment = await res.json();
      setAppointments((prev) =>
        prev.map((a) =>
          a.id === updatedAppointment.id ? updatedAppointment : a
        )
      );
      closeInviteModal();
    } catch (err: any) {
      setInviteError(err.message || "Failed to send invitation.");
    } finally {
      setInviting(false);
    }
  };

  const openAssignModal = async (appointmentId: number) => {
    setAssignAppointmentId(appointmentId);
    setAssignError(null);
    setAssignModalOpen(true);
    setSelectedStudentId(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/assignable-students`,
        {
          credentials: "include",
        }
      );
      const data = await res.json();
      if (Array.isArray(data)) {
        setAssignableStudents(data);
      } else {
        setAssignableStudents([]);
        setAssignError("No assignable users found.");
      }
    } catch {
      setAssignableStudents([]);
      setAssignError("Failed to load assignable users.");
    }
  };

  const assignAppointment = async () => {
    if (!assignAppointmentId || !selectedStudentId) return;
    setAssigning(true);
    setAssignError(null);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments/${assignAppointmentId}/assign`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ studentId: selectedStudentId }),
        }
      );
      if (!res.ok) throw new Error("Failed to assign appointment");
      const updated = await res.json();
      setAppointments((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );
      setAssignModalOpen(false);
    } catch (err: any) {
      setAssignError(err.message || "Assignment failed.");
    } finally {
      setAssigning(false);
    }
  };

  const handleMarkAsCompleted = async (a: any) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments/${a.id}/complete`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );

      if (!res.ok) throw new Error("Failed to mark appointment as completed");

      const updatedAppointment = await res.json();
      setAppointments((prev) =>
        prev.map((item) =>
          item.id === updatedAppointment.id ? updatedAppointment : item
        )
      );

      alert("✅ Appointment marked as completed.");
    } catch (err) {
      alert("❌ Error updating appointment.");
    }
  };

  const openPaymentModal = (payment: any) => {
    setSelectedPayment(payment);
    setPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedPayment(null);
  };

  const openApproveModal = (appointmentId: number) => {
    setApproveConfirmId(appointmentId);
    setApproveConfirmModalOpen(true);
  };

  const approvePayment = async () => {
    if (!approveConfirmId) return;
    setApproveConfirmModalOpen(false);
    setPaymentActionLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments/${approveConfirmId}/approve-payment`,
        { method: "PUT", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to approve payment");
      const updated = await res.json();
      setAppointments((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );
    } catch {
      alert("Failed to approve payment.");
    } finally {
      setPaymentActionLoading(false);
      setApproveConfirmId(null);
    }
  };

  const openRejectModal = (appointmentId: number) => {
    setRejectAppointmentId(appointmentId);
    setRejectReason("");
    setRejectModalOpen(true);
  };

  const submitRejectPayment = async () => {
    if (!rejectAppointmentId) return;
    setPaymentActionLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments/${rejectAppointmentId}/reject-payment`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ reason: rejectReason }),
        }
      );
      if (!res.ok) throw new Error("Failed to reject payment");
      const updated = await res.json();
      setAppointments((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );
      setRejectModalOpen(false);
      setRejectAppointmentId(null);
      setRejectReason("");
    } catch {
      alert("Failed to reject payment.");
    } finally {
      setPaymentActionLoading(false);
    }
  };

  const openRevertModal = (appointmentId: number) => {
    setRevertConfirmId(appointmentId);
    setRevertConfirmModalOpen(true);
  };

  const revertPayment = async () => {
    if (!revertConfirmId) return;
    setRevertConfirmModalOpen(false);
    setPaymentActionLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/appointments/${revertConfirmId}/revert-payment`,
        { method: "PUT", credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to revert payment");
      const updated = await res.json();
      setAppointments((prev) =>
        prev.map((a) => (a.id === updated.id ? updated : a))
      );
    } catch {
      alert("Failed to revert payment decision.");
    } finally {
      setPaymentActionLoading(false);
      setRevertConfirmId(null);
    }
  };

  const viewPaymentSlip = async (paymentId: number) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/${paymentId}/slip`,
        { credentials: "include" }
      );
      if (!res.ok) throw new Error("Failed to get slip URL");
      const data = await res.json();
      if (!data.hasSlip || !data.downloadUrl) {
        alert("No payment slip available.");
        return;
      }
      window.open(data.downloadUrl, "_blank");
    } catch {
      alert("Could not load payment slip.");
    }
  };

  function formatAppointmentTime(dtString: string | number | Date) {
    if (!dtString) return "-";
    const dt = new Date(dtString);
    const day = String(dt.getDate()).padStart(2, "0");
    const month = String(dt.getMonth() + 1).padStart(2, "0");
    const year = dt.getFullYear();
    let hour = dt.getHours();
    const minute = String(dt.getMinutes()).padStart(2, "0");
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    hour = hour ? hour : 12;
    return `${day}-${month}-${year} T${hour}:${minute}${ampm}`;
  }

  const PaginationControls = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border">
      {/* Items per page */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Show:</span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-gray-600">per page</span>
      </div>

      {/* Page info */}
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1} to{" "}
        {Math.min(endIndex, filteredAppointments.length)} of{" "}
        {filteredAppointments.length} appointments
      </div>

      {/* Page buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronLeft />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                  currentPage === pageNum
                    ? "bg-[#009fe3] text-white border-[#009fe3]"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronRight />
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Last
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        icon={<FaCalendarAlt />}
        title="Manage Appointments"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: "Manage Appointments" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={fetchAppointments}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-[#009fe3]/10 hover:bg-[#009fe3]/20 text-[#009fe3] rounded-lg transition-colors disabled:opacity-50"
        >
          <FaSync className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Report summary */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white border rounded shadow flex flex-col items-center py-4">
            <span className="text-sm text-gray-500">Total</span>
            <span className="text-xl font-bold text-[#009fe3]">{total}</span>
          </div>
          <button
            onClick={() => setActiveTab("payments")}
            className="bg-orange-50 border-2 border-orange-300 rounded shadow flex flex-col items-center py-4 hover:bg-orange-100 transition-colors cursor-pointer w-full"
            title="Click to review pending payments"
          >
            <FaHourglassHalf className="text-orange-500 mb-1" />
            <span className="text-sm text-gray-500">Pending Payment</span>
            <span className="text-lg font-bold text-orange-700">{totalPendingPayment}</span>
            {totalPendingPayment > 0 && (
              <span className="mt-1 text-xs text-orange-600 font-semibold underline">Review →</span>
            )}
          </button>
          <div className="bg-yellow-50 border rounded shadow flex flex-col items-center py-4">
            <FaHourglassHalf className="text-yellow-500 mb-1" />
            <span className="text-sm text-gray-500">Pending</span>
            <span className="text-lg font-bold text-yellow-700">
              {totalPending}
            </span>
          </div>
          <div className="bg-blue-50 border rounded shadow flex flex-col items-center py-4">
            <FaCheckCircle className="text-blue-500 mb-1" />
            <span className="text-sm text-gray-500">Invited</span>
            <span className="text-lg font-bold text-blue-700">
              {totalInvited}
            </span>
          </div>
          <div className="bg-green-50 border rounded shadow flex flex-col items-center py-4">
            <FaCheckCircle className="text-green-500 mb-1" />
            <span className="text-sm text-gray-500">Completed</span>
            <span className="text-lg font-bold text-green-700">
              {totalCompleted}
            </span>
          </div>
          <div className="bg-red-50 border rounded shadow flex flex-col items-center py-4">
            <FaTimesCircle className="text-red-500 mb-1" />
            <span className="text-sm text-gray-500">Cancelled</span>
            <span className="text-lg font-bold text-red-700">
              {totalCancelled}
            </span>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("payments")}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === "payments"
                ? "border-orange-500 text-orange-600 bg-white"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Payment Review
            {totalPendingPayment > 0 && (
              <span className="bg-orange-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                {totalPendingPayment}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("appointments")}
            className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors ${
              activeTab === "appointments"
                ? "border-[#009fe3] text-[#009fe3] bg-white"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            All Appointments
          </button>
        </div>

        {/* ── Payment Review Tab ── */}
        {activeTab === "payments" && (
          <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
            {appointments.filter((a) => a.status === "PENDING_PAYMENT").length === 0 ? (
              <div className="p-12 text-center text-gray-400">
                No pending payments to review.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-orange-500 text-white">
                    <tr>
                      <th className="px-4 py-3">ID</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Phone</th>
                      <th className="px-4 py-3">Topic</th>
                      <th className="px-4 py-3">Method</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Transaction / Ref</th>
                      <th className="px-4 py-3">Submitted</th>
                      <th className="px-4 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {appointments
                      .filter((a) => a.status === "PENDING_PAYMENT")
                      .sort((a, b) => b.id - a.id)
                      .map((a) => (
                        <tr key={a.id} className="hover:bg-orange-50">
                          <td className="px-4 py-3 font-medium">{a.id}</td>
                          <td className="px-4 py-3">{a.userName}</td>
                          <td className="px-4 py-3 text-xs">{a.userEmail}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{a.userPhone}</td>
                          <td className="px-4 py-3">{a.appointmentTopic}</td>
                          <td className="px-4 py-3">
                            {a.payment ? (
                              <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                a.payment.paymentMethod === "bkash"
                                  ? "bg-pink-100 text-pink-800"
                                  : "bg-blue-100 text-blue-800"
                              }`}>
                                {a.payment.paymentMethod}
                              </span>
                            ) : "-"}
                          </td>
                          <td className="px-4 py-3 font-semibold">
                            {a.payment ? `${a.payment.amount} BDT` : "-"}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600">
                            {a.payment?.transactionId || a.payment?.referenceNumber || "-"}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                            {a.payment?.submittedAt
                              ? new Date(a.payment.submittedAt).toLocaleString()
                              : "-"}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5 items-center">
                              {a.paymentId && (
                                <button
                                  onClick={() => viewPaymentSlip(a.paymentId)}
                                  className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition text-xs w-full"
                                >
                                  View Slip
                                </button>
                              )}
                              <button
                                onClick={() => openApproveModal(a.id)}
                                disabled={paymentActionLoading}
                                className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition text-xs w-full disabled:opacity-50"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => openRejectModal(a.id)}
                                disabled={paymentActionLoading}
                                className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition text-xs w-full disabled:opacity-50"
                              >
                                Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── All Appointments Tab ── */}
        {activeTab === "appointments" && <>

        {/* Toggle and Filters Row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          {/* Table/Card Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${
                viewMode === "table" ? "bg-[#009fe3] text-white" : "bg-gray-200"
              }`}
              aria-label="Table View"
            >
              <FaTable size={20} />
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${
                viewMode === "card" ? "bg-[#009fe3] text-white" : "bg-gray-200"
              }`}
              aria-label="Card View"
            >
              <FaTh size={20} />
            </button>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="mr-2 font-medium text-gray-700">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded px-3 py-1"
                style={{ fontSize: "16px" }}
              >
                <option value="ALL">All</option>
                <option value="PENDING_PAYMENT">Pending Payment</option>
                <option value="PENDING">Pending</option>
                <option value="INVITED">Invited</option>
                <option value="CONFIRMED">Confirmed</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="mr-2 font-medium text-gray-700">Topic:</label>
              <select
                value={topicFilter}
                onChange={(e) => setTopicFilter(e.target.value)}
                className="border rounded px-3 py-1"
                style={{ fontSize: "16px" }}
              >
                <option value="ALL">All</option>
                {allTopics.map((topic) => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table or Card View */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
            <p className="text-gray-600">Loading appointments...</p>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center text-gray-400">
            No appointments found.
          </div>
        ) : viewMode === "table" ? (
          <>
            <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-[#009fe3] text-white">
                    <tr>
                      <th className="px-4 py-3">ID</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-2 py-3 w-24 whitespace-nowrap">
                        Phone
                      </th>
                      <th className="px-4 py-3">Topic</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Payment</th>
                      <th className="px-4 py-3">Time</th>
                      <th className="px-4 py-3">Assigned To</th>
                      <th className="px-2 py-3 w-36 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedAppointments.map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{a.id}</td>
                        <td className="px-4 py-2">{a.userName}</td>
                        <td className="px-4 py-2">{a.userEmail}</td>
                        <td className="px-2 py-2 w-24 whitespace-nowrap truncate">
                          {a.userPhone}
                        </td>
                        <td className="px-4 py-2">{a.appointmentTopic}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${
                              statusColors[a.status] ||
                              "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {a.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">
                          {a.payment ? (
                            `${a.payment.paymentMethod} (${a.payment.amount})`
                          ) : (
                            <span className="text-red-500">Unpaid</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {formatAppointmentTime(a.appointmentTime)}
                        </td>
                        <td className="px-4 py-2">
                          {a.assignedTo
                            ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}`
                            : "-"}
                        </td>
                        <td className="px-2 py-2 w-36">
                          <div className="flex flex-col gap-2 mt-4">
                            {a.status === "PENDING_PAYMENT" && (
                              <>
                                {a.paymentId && (
                                  <button
                                    onClick={() => viewPaymentSlip(a.paymentId)}
                                    className="bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition w-full text-xs"
                                    type="button"
                                  >
                                    View Slip
                                  </button>
                                )}
                                <button
                                  onClick={() => openApproveModal(a.id)}
                                  disabled={paymentActionLoading}
                                  className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition w-full text-xs disabled:opacity-50"
                                  type="button"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => openRejectModal(a.id)}
                                  disabled={paymentActionLoading}
                                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition w-full text-xs disabled:opacity-50"
                                  type="button"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            {(a.status === "PENDING" || a.status === "CANCELLED") && (
                              <button
                                onClick={() => openRevertModal(a.id)}
                                disabled={paymentActionLoading}
                                className="bg-yellow-500 text-white px-3 py-1 rounded hover:bg-yellow-600 transition w-full text-xs disabled:opacity-50"
                                type="button"
                                title="Revert payment decision back to Pending Payment"
                              >
                                ↩ Revert Payment
                              </button>
                            )}
                            <button
                              onClick={() => openAssignModal(a.id)}
                              className={`bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition w-full ${
                                a.status === "COMPLETED" || a.status === "PENDING_PAYMENT"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              disabled={a.status === "COMPLETED" || a.status === "PENDING_PAYMENT"}
                              type="button"
                            >
                              Assign
                            </button>

                            <button
                              onClick={() => openInviteModal(a.id, a)}
                              className={`px-3 py-1 w-full rounded transition text-sm font-medium text-white ${
                                a.assignedTo && a.status !== "PENDING_PAYMENT"
                                  ? "bg-[#009fe3] hover:bg-[#007bbf]"
                                  : "bg-gray-300 cursor-not-allowed"
                              } ${
                                a.status === "COMPLETED"
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                              disabled={
                                !a.assignedTo || a.status === "COMPLETED" || a.status === "PENDING_PAYMENT"
                              }
                              type="button"
                            >
                              {a.status === "INVITED"
                                ? "Edit Invite"
                                : "Invite"}
                            </button>

                            {a.status === "CONFIRMED" && a.meetingLink && (
                              <a
                                href={
                                  a.meetingLink.startsWith("http://") ||
                                  a.meetingLink.startsWith("https://")
                                    ? a.meetingLink
                                    : `https://${a.meetingLink}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block w-full text-center bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition text-sm mt-2"
                              >
                                Start Meeting
                              </a>
                            )}
                            {a.status && a.status.trim() === "CONFIRMED" && (
                              <button
                                onClick={() => handleMarkAsCompleted(a)}
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded w-full transition text-sm font-medium mt-2"
                                type="button"
                              >
                                Mark as Completed
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile scroll hint */}
              <div className="md:hidden bg-gray-50 border-t border-gray-200 px-4 py-2 text-center">
                <p className="text-xs text-gray-500">
                  ← Scroll horizontally to see all columns →
                </p>
              </div>
            </div>

            {/* Pagination Controls */}
            {paginatedAppointments.length > 0 && <PaginationControls />}
          </>
        ) : (
          <>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {paginatedAppointments.map((a) => {
                const isCompleted = a.status === "COMPLETED";
                const isStartingSoon = (() => {
                  const appointmentTime = new Date(a.appointmentTime);
                  const now = new Date();
                  const diffMinutes =
                    (appointmentTime.getTime() - now.getTime()) / (1000 * 60);
                  return diffMinutes >= 0 && diffMinutes <= 30;
                })();

                return (
                  <div
                    key={a.id}
                    className={`bg-white rounded-lg shadow border p-6 hover:shadow-lg transition-shadow ${
                      isStartingSoon ? "bg-yellow-100" : ""
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-[#009fe3]">
                          {a.userName}
                        </h3>
                        <p className="text-sm text-gray-600">{a.userEmail}</p>
                        <p className="text-sm text-gray-600">{a.userPhone}</p>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          statusColors[a.status] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {a.status}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Topic:</span>
                        <span className="font-medium">
                          {a.appointmentTopic}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Payment:</span>
                        <span
                          className={
                            a.payment ? "text-green-600" : "text-red-600"
                          }
                        >
                          {a.payment ? (
                            <>
                              {`${a.payment.paymentMethod} (${a.payment.amount})`}
                              <button
                                onClick={() => openPaymentModal(a.payment)}
                                className="ml-2 px-2 py-1 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 transition"
                              >
                                View Payment
                              </button>
                            </>
                          ) : (
                            "Unpaid"
                          )}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Scheduled:</span>
                        <span>{formatAppointmentTime(a.appointmentTime)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Assigned To:</span>
                        <span>
                          {a.assignedTo
                            ? `${a.assignedTo.firstName} ${a.assignedTo.lastName}`
                            : "-"}
                        </span>
                      </div>
                    </div>
                    {a.status === "PENDING_PAYMENT" && (
                      <div className="mt-4 flex flex-col gap-2">
                        {a.paymentId && (
                          <button
                            onClick={() => viewPaymentSlip(a.paymentId)}
                            className="w-full bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition text-sm"
                            type="button"
                          >
                            View Payment Slip
                          </button>
                        )}
                        <button
                          onClick={() => openApproveModal(a.id)}
                          disabled={paymentActionLoading}
                          className="w-full bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition text-sm disabled:opacity-50"
                          type="button"
                        >
                          Approve Payment
                        </button>
                        <button
                          onClick={() => openRejectModal(a.id)}
                          disabled={paymentActionLoading}
                          className="w-full bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition text-sm disabled:opacity-50"
                          type="button"
                        >
                          Reject Payment
                        </button>
                      </div>
                    )}

                    {(a.status === "PENDING" || a.status === "CANCELLED") && (
                      <button
                        onClick={() => openRevertModal(a.id)}
                        disabled={paymentActionLoading}
                        className="mt-3 w-full bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-1 rounded transition text-sm disabled:opacity-50"
                        type="button"
                        title="Revert payment decision back to Pending Payment"
                      >
                        ↩ Revert Payment Decision
                      </button>
                    )}

                    <button
                      onClick={() => openAssignModal(a.id)}
                      className={`mt-4 w-full px-3 py-1 rounded transition text-sm font-medium ${
                        isCompleted || a.status === "PENDING_PAYMENT"
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "bg-green-600 text-white hover:bg-green-700"
                      }`}
                      disabled={isCompleted || a.status === "PENDING_PAYMENT"}
                    >
                      Assign
                    </button>

                    <button
                      onClick={() => openInviteModal(a.id, a)}
                      className={`mt-2 w-full px-3 py-1 rounded transition text-sm font-medium ${
                        !a.assignedTo || isCompleted || a.status === "PENDING_PAYMENT"
                          ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                          : "bg-[#009fe3] text-white hover:bg-[#007bbf]"
                      }`}
                      disabled={!a.assignedTo || isCompleted || a.status === "PENDING_PAYMENT"}
                    >
                      {a.status === "INVITED" ? "Edit Invite" : "Invite"}
                    </button>

                    {a.status === "CONFIRMED" && a.meetingLink && (
                      <a
                        href={
                          a.meetingLink.startsWith("http://") ||
                          a.meetingLink.startsWith("https://")
                            ? a.meetingLink
                            : `https://${a.meetingLink}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-block w-full text-center bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition text-sm"
                      >
                        Start Meeting
                      </a>
                    )}

                    {a.status === "CONFIRMED" && (
                      <button
                        onClick={() => handleMarkAsCompleted(a)}
                        className="mt-2 w-full bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded transition text-sm font-medium"
                        type="button"
                      >
                        Mark as Completed
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination for card view */}
            {paginatedAppointments.length > 0 && <PaginationControls />}
          </>
        )}

        </> /* end activeTab === "appointments" */}

        {/* Invitation Modal */}
        {inviteModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
              <h2 className="text-xl font-semibold mb-4">Send Invitation</h2>
              <label
                className="block mb-1 font-medium"
                htmlFor="appointmentTime"
              >
                Appointment Date & Time
              </label>
              <input
                type="datetime-local"
                id="appointmentTime"
                name="appointmentTime"
                value={invitationData.appointmentTime}
                onChange={handleInviteChange}
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                style={{ fontSize: "16px" }}
              />
              <label className="block mb-1 font-medium" htmlFor="meetingLink">
                Meeting Link
              </label>
              <input
                type="url"
                id="meetingLink"
                name="meetingLink"
                value={invitationData.meetingLink}
                onChange={handleInviteChange}
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                style={{ fontSize: "16px" }}
                placeholder="https://zoom.us/..."
              />
              {inviteError && typeof inviteError === "string" && (
                <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 font-medium">
                  {inviteError}
                </div>
              )}
              <div className="flex justify-end gap-4">
                <button
                  onClick={closeInviteModal}
                  className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition"
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  onClick={sendInvitation}
                  className="px-4 py-2 rounded bg-[#009fe3] text-white hover:bg-[#007bbf] transition"
                  disabled={inviting}
                >
                  {inviting
                    ? "Sending..."
                    : invitationData.appointmentTime &&
                      invitationData.meetingLink
                    ? "Update Invitation"
                    : "Send Invitation"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assign Modal */}
        {assignModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
              <h2 className="text-xl font-semibold mb-4">Assign Appointment</h2>
              <label className="block mb-1 font-medium" htmlFor="assignStudent">
                Assign To
              </label>
              <select
                id="assignStudent"
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4"
                style={{ fontSize: "16px" }}
                value={selectedStudentId || ""}
                onChange={(e) => setSelectedStudentId(Number(e.target.value))}
              >
                <option value="">Select user</option>
                {Array.isArray(assignableStudents) &&
                  assignableStudents.map((s) => (
                    <option key={s.studentId} value={s.studentId}>
                      {s.firstName} {s.lastName} ({s.role})
                    </option>
                  ))}
              </select>
              {assignError && (
                <div className="text-red-600 mb-4 font-medium">
                  {assignError}
                </div>
              )}
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setAssignModalOpen(false)}
                  className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition"
                  disabled={assigning}
                >
                  Cancel
                </button>
                <button
                  onClick={assignAppointment}
                  className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 transition"
                  disabled={assigning || !selectedStudentId}
                >
                  {assigning ? "Assigning..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Payment Modal */}
        {rejectModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
              <h2 className="text-xl font-semibold mb-4">Reject Payment</h2>
              <p className="text-gray-600 mb-3 text-sm">
                Optionally provide a reason for rejection. The appointment will be cancelled.
              </p>
              <label className="block mb-1 font-medium" htmlFor="rejectReasonInput">
                Reason (optional)
              </label>
              <textarea
                id="rejectReasonInput"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 mb-4 resize-none"
                rows={3}
                placeholder="e.g. Payment amount incorrect, unreadable slip..."
                style={{ fontSize: "16px" }}
              />
              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setRejectModalOpen(false);
                    setRejectAppointmentId(null);
                    setRejectReason("");
                  }}
                  className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition"
                  disabled={paymentActionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={submitRejectPayment}
                  className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50"
                  disabled={paymentActionLoading}
                >
                  {paymentActionLoading ? "Rejecting..." : "Reject Payment"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Approve Payment Confirmation Modal */}
        {approveConfirmModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
              <h2 className="text-lg font-semibold mb-2">Approve Payment?</h2>
              <p className="text-gray-600 text-sm mb-6">
                This will mark the payment as approved and move the appointment to Pending status.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setApproveConfirmModalOpen(false); setApproveConfirmId(null); }}
                  className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition"
                  disabled={paymentActionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={approvePayment}
                  className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-50"
                  disabled={paymentActionLoading}
                >
                  {paymentActionLoading ? "Approving..." : "Approve"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revert Payment Confirmation Modal */}
        {revertConfirmModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-sm w-full p-6">
              <h2 className="text-lg font-semibold mb-2">Revert Payment Decision?</h2>
              <p className="text-gray-600 text-sm mb-6">
                Status will go back to <strong>Pending Payment</strong>. The revenue will be removed from finance totals.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setRevertConfirmModalOpen(false); setRevertConfirmId(null); }}
                  className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 transition"
                  disabled={paymentActionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={revertPayment}
                  className="px-4 py-2 rounded bg-yellow-500 text-white hover:bg-yellow-600 transition disabled:opacity-50"
                  disabled={paymentActionLoading}
                >
                  {paymentActionLoading ? "Reverting..." : "Revert"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Payment Modal: Only show in grid/card view */}
        {viewMode === "card" && paymentModalOpen && selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
              <h2 className="text-xl font-semibold mb-4">
                Payment Information
              </h2>
              <div className="mb-2">
                <strong>Method:</strong> {selectedPayment.paymentMethod}
              </div>
              <div className="mb-2">
                <strong>Amount:</strong> {selectedPayment.amount}
              </div>
              {selectedPayment.paymentMethod === "bkash" ? (
                <>
                  <div className="mb-2">
                    <strong>Transaction ID:</strong>{" "}
                    {selectedPayment.transactionId || "-"}
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-2">
                    <strong>Bank Name:</strong>{" "}
                    {selectedPayment.bankName || "-"}
                  </div>
                  <div className="mb-2">
                    <strong>Branch Name:</strong>{" "}
                    {selectedPayment.branchName || "-"}
                  </div>
                  <div className="mb-2">
                    <strong>Reference Number:</strong>{" "}
                    {selectedPayment.referenceNumber || "-"}
                  </div>
                  <div className="mb-2">
                    <strong>Payment Date:</strong>{" "}
                    {selectedPayment.paymentDate
                      ? new Date(
                          selectedPayment.paymentDate
                        ).toLocaleDateString()
                      : "-"}
                  </div>
                </>
              )}
              <button
                onClick={closePaymentModal}
                className="mt-4 w-full bg-[#009fe3] text-white py-2 rounded hover:bg-[#007bbf] transition"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageAppointments;
